class PaginationTheme extends HTMLElement {
  constructor() {
    super();

    this.progress = this.querySelector(".pagination-progress");
    this.button = this.querySelector(".load-more");
    
    // Auto-detect if current page is product or blog
    this.isProductPage = document.getElementById("product-grid") !== null;
    this.isBlogPage = document.getElementById("article-grid") !== null;
    
    // Set container based on page type
    if (this.isProductPage) {
      this.grid = document.getElementById("product-grid");
      this.containerSelector = "#ProductGridContainer";
      this.itemSelector = "product-card";
    } else if (this.isBlogPage) {
      this.grid = document.getElementById("article-grid");
      this.containerSelector = "#BlogGridContainer";
      this.itemSelector = ".cmb-article-grid__item";
    }else {
      // No supported grid found - pagination not applicable
      console.warn('PaginationTheme: No product-grid or article-grid found');
      return;
    }

    this.container = document.querySelector(this.containerSelector);
    this.pageSizeMobile = this.container && this.container.dataset.pageSizeMobile
      ? parseInt(this.container.dataset.pageSizeMobile, 10)
      : 8;
    this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    this.mobileDisplayedCount = this.pageSizeMobile;
    this.hasUserExpandedMobile = false;
    this.pendingMobileTarget = null;
    
    this.total_page = this.dataset.totalPage;    
    this.i = this.dataset.currentPage ? parseInt(this.dataset.currentPage) : 1;
    this.current_page = this.dataset.currentPage ? parseInt(this.dataset.currentPage) : 1;

    if (this.isProductPage) {
      this.mobileMediaQuery.addEventListener('change', this.handleViewportChange = this.debounce(() => this.onViewportChange(), 150));
      if (this.shouldUseMobilePagination()) {
       this.applyMobileVisibility();
      }
    }
    
    this.renderItemCount();
    this.loadMore()
    
  }

  shouldUseMobilePagination() {
    return this.isProductPage && this.mobileMediaQuery.matches;
  }

  getGridItems() {
    return Array.from(this.grid.querySelectorAll(':scope > .grid__item'));
  }

  getVisibleItemCount() {
    if (this.shouldUseMobilePagination()) {
      return this.getGridItems().filter((item) => !item.classList.contains('mobile-pagination-hidden')).length;
    }

    return document.querySelectorAll(`${this.containerSelector} ${this.itemSelector}`).length;
  }

  applyMobileVisibility() {
    if (!this.isProductPage) {
      return;
    }

    const items = this.getGridItems();
    items.forEach((item, index) => {
      item.classList.toggle('mobile-pagination-hidden', index >= this.mobileDisplayedCount);
    });
  }

  onViewportChange() {
    if (!this.isProductPage) {
      return;
    }

    if (this.shouldUseMobilePagination()) {
      // Re-entering mobile: reset only if user has not actively expanded yet,
      // otherwise preserve the previously revealed progress
      if (!this.hasUserExpandedMobile) {
        this.mobileDisplayedCount = this.pageSizeMobile;
      }
      this.applyMobileVisibility();
    } else {
      // Switching to desktop: keep mobileDisplayedCount intact so progress
      // is restored when the user resizes back, just clear pending target
      this.pendingMobileTarget = null;
      this.getGridItems().forEach((item) => item.classList.remove('mobile-pagination-hidden'));
    }

    this.renderItemCount();
  }

  debounce(fn, wait) {
    let timer = null;
    return function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  getTotalCount() {
    return parseInt(this.progress.dataset.totalNum, 10);
  }

  hasMoreServerPages() {
    return parseInt(this.dataset.currentPage, 10) < parseInt(this.total_page, 10);
  }

  renderItemCount(){
    const visibleCount = this.getVisibleItemCount();
    const totalCount = this.getTotalCount();

    this.progress.querySelector(".num").innerText = visibleCount;
    this.progress.style.setProperty("--current-progress", visibleCount * 100.0 / totalCount + "%");

    if (visibleCount >= totalCount) {
      this.classList.add("no-more")
    } else {
      this.classList.remove("no-more")
    }
  }

  addUrlParam(search, key) {
      var newParam = key + '=' + this.dataset.currentPage,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }
      this.current_page = newParam;
      return params;
    }

  loadItems() {
    if (this.getAttribute('loading')) {
      return;
    }
    this.i = parseInt(this.dataset.currentPage);
    this.i++;
    this.dataset.currentPage = this.i;
    let url = document.location.pathname + this.addUrlParam(document.location.search, 'page');
    this.setAttribute('loading', true);
    
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;
        this.renderItems(html, url);
      });
  }

  loadMoreMobile() {
    const totalCount = this.getTotalCount();
    const targetCount = Math.min(this.mobileDisplayedCount + this.pageSizeMobile, totalCount);
    const items = this.getGridItems();

    // Mark user as actively expanded so viewport switches preserve progress
    this.hasUserExpandedMobile = true;

    if (items.length >= targetCount) {
      this.mobileDisplayedCount = targetCount;
      this.applyMobileVisibility();
      this.renderItemCount();
      return;
    }

    if (this.hasMoreServerPages()) {
      this.pendingMobileTarget = targetCount;
      this.loadItems();
      return;
    }

    this.mobileDisplayedCount = items.length;
    this.applyMobileVisibility();
    this.renderItemCount();
  }

  renderItems(html, url) {
    // Select correct grid ID based on page type
    const gridId = this.isProductPage ? 'product-grid' : 'article-grid';
    const grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById(gridId);
    
    if (grid_to_replace) {
      this.grid.insertAdjacentHTML('beforeend', grid_to_replace.innerHTML);
    }

    if (this.shouldUseMobilePagination() && this.pendingMobileTarget) {
      const items = this.getGridItems();

      if (items.length < this.pendingMobileTarget && this.hasMoreServerPages()) {
        this.removeAttribute('loading');
        this.loadItems();
        return;
      }

      this.mobileDisplayedCount = Math.min(this.pendingMobileTarget, items.length);
      this.pendingMobileTarget = null;
      this.applyMobileVisibility();
    }
    
    this.renderItemCount();
    this.removeAttribute('loading');
    
    // Product page: reinitialize Klaviyo
    if (this.isProductPage && typeof klaviyoBIS !== 'undefined') {
      klaviyoBIS.init();
    }
  }

  loadMore() {
      this.button.addEventListener('click', () => {
        if (this.shouldUseMobilePagination()) {
          this.loadMoreMobile();
        } else {
          this.loadItems();
        }
        this.blur();
        return false;
      });
    }
  
  initEventListener() {
    
  }

}

customElements.define('pagination-theme', PaginationTheme);
